import React from 'react';
import { useQuery, useSaveDelete } from '../graphql/hooks';
import SimpleTable from '../teal-react/components/organisms/SimpleTable';
import Loading from '../teal-react/components/molecules/Loading';
import SimpleGrid from '../teal-react/components/organisms/SimpleGrid';
import { IconButton } from '@mui/material';
import { Add, Check, DeleteOutline, Edit } from '@mui/icons-material';
import SimpleModal from '../teal-react/components/organisms/SimpleModal';
import TestParameterForm from './TestParameterForm';
import SimpleDeleteConfirm from '../teal-react/components/organisms/SimpleDeleteConfirm';
import ExcludeReadOnly from './Auth/ExcludeReadOnly';
import { useAuth } from '../teal-react/utils/token';

interface TestingSetupManagerProps {
  product_id: number;
}
const TestingSetupManager = ({ product_id }: TestingSetupManagerProps) => {
  const { user } = useAuth();
  const [editParameter, setEditParameter] = React.useState<any>();
  const [deleteId, setDeleteId] = React.useState(0);
  const variables = { where: JSON.stringify({ product_id }) };
  const [saveTestParameter, deleteParam] = useSaveDelete(
    'product_test_parameter',
    ['product_specification', 'product_test_parameter']
  );
  const { data } = useQuery(
    `query ProductSpecs($where: String) {
      product_specification(where: $where){
        id
        expectation
        specification: specification_id_specification{
          id
          name
        }
      }
    }`,
    {
      variables,
    }
  );
  const { data: tests } = useQuery(
    `query TestSpecs($where: String){
      product_test_parameter(where: $where, order: "ordinal"){
        id
        parameter
        coa
        ordinal
        specification_id
        specification: specification_id_specification{
          id
          name
        }
      }
    }`,
    { variables }
  );
  if (!data || !tests) {
    return <Loading />;
  }
  const got_it = tests.product_test_parameter.map(
    (r: any) => r.specification.id
  );
  return (
    <>
      <SimpleGrid
        items={[
          {
            size: 6,
            content: (
              <SimpleTable
                header="Product Specifications"
                row_limit={9999}
                data={data.product_specification}
                columns={[
                  {
                    header: 'Attribute',
                    cell: (r: any) => r.specification.name,
                  },
                  {
                    header: 'Expectation',
                    cell: (r: any) => r.expectation,
                  },
                  {
                    header: '',
                    cell: (r: any) => {
                      if (got_it.includes(r.specification.id)) {
                        return (
                          <div
                            style={{ padding: '1px 5px' }}
                            title="Specification Included"
                          >
                            <Check color="success" />
                          </div>
                        );
                      }
                      return (
                        <ExcludeReadOnly>
                          <IconButton
                            size="small"
                            title="Add Specification to Testing"
                            onClick={() => {
                              saveTestParameter(
                                {
                                  product_id,
                                  specification_id: r.specification.id,
                                  parameter: r.expectation,
                                  coa: 0,
                                  ordinal:
                                    tests.product_test_parameter.length + 1,
                                },
                                true
                              );
                            }}
                          >
                            <Add color="primary" />
                          </IconButton>
                        </ExcludeReadOnly>
                      );
                    },
                  },
                ]}
              />
            ),
          },
          {
            size: 6,
            content: (
              <SimpleTable
                header="Testing Specifications"
                row_limit={9999}
                data={tests.product_test_parameter}
                headeraction={
                  user?.isRO
                    ? null
                    : {
                        icon: <Add color="primary" />,
                        onClick: () =>
                          setEditParameter({
                            product_id,
                            ordinal: tests.product_test_parameter.length,
                            coa: 0,
                          }),
                      }
                }
                columns={[
                  {
                    header: 'Attribute',
                    cell: (r: any) => r.specification.name,
                  },
                  {
                    header: 'Expectation',
                    cell: (r: any) => r.parameter,
                  },
                  {
                    header: 'COA',
                    cell: (r: any) => (r.coa ? 'Yes' : ''),
                  },
                  {
                    header: '',
                    sort: false,
                    style: { width: 70 },
                    cell: (r: any) => {
                      if (user?.isRO) {
                        return null;
                      }
                      return (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setEditParameter(r)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(r.id)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </>
                      );
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />
      {deleteId ? (
        <SimpleDeleteConfirm
          onCancel={() => setDeleteId(0)}
          onDelete={() => {
            deleteParam({ id: deleteId });
            setDeleteId(0);
          }}
        />
      ) : null}
      {editParameter ? (
        <SimpleModal
          size="md"
          open={true}
          onClose={() => setEditParameter(null)}
        >
          <TestParameterForm
            model={editParameter}
            onClose={() => setEditParameter(null)}
          />
        </SimpleModal>
      ) : null}
    </>
  );
};

export default TestingSetupManager;
